import * as React from "react";
import { Combobox } from "@base-ui/react/combobox";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSeedsPortalContainer } from "./SeedsPortal";
import { useSuppressEscapeClear } from "./Common/useSuppressEscapeClear";
import ComboboxItem from "./Common/ComboboxItem";
import VirtualizedComboboxList from "./Common/VirtualizedComboboxList";
import type { DataWithId } from "./Common/types";
import { groupItems } from "./Common/groupItems";
import { Field } from "./Common/SelectStyles";
import {
  ComboboxInputGroup,
  ComboboxInput,
  ComboboxActionButtons,
  ComboboxClear,
  ComboboxTrigger,
  ComboboxPositioner,
  ComboboxPopup,
  ComboboxList,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxSeparator,
} from "./Common/ComboboxStyles";
import { ClearIcon, StyledChevron, ChevronIcon } from "./Common/SelectIcons";

// ─── Props ────────────────────────────────────────────────────────────────────

interface SingleComboboxBaseProps {
  id?: string;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  filter?: ((item: unknown, query: string) => boolean) | null;
  "aria-label"?: string;
  /** Id of a visible label. Preferred over `aria-label`. */
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  isInvalid?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

interface SingleComboboxDataBaseProps<T extends DataWithId>
  extends SingleComboboxBaseProps {
  data: T[];
  itemToString: (item: T | null) => string;
  renderItem?: (item: T) => React.ReactNode;
  inputType?: "icon" | "radio" | "checkbox" | "none";
  selectedItemId?: T["id"] | null;
  defaultSelectedItemId?: T["id"] | null;
  onSelectedItemIdChange?: (id: T["id"] | null) => void;
  children?: never;
}

interface SingleComboboxDataProps<T extends DataWithId>
  extends SingleComboboxDataBaseProps<T> {
  isVirtualized?: false;
  groupBy?: (item: T) => string;
  renderGroupHeading?: (label: string) => React.ReactNode;
}

interface SingleComboboxVirtualizedProps<T extends DataWithId>
  extends SingleComboboxDataBaseProps<T> {
  isVirtualized: true;
  groupBy?: never;
  renderGroupHeading?: never;
}

interface SingleComboboxChildrenProps extends SingleComboboxBaseProps {
  children: React.ReactNode;
  selectedItemId?: string | number | null;
  defaultSelectedItemId?: string | number | null;
  onSelectedItemIdChange?: (id: string | number | null) => void;
  data?: never;
  itemToString?: never;
  renderItem?: never;
  inputType?: never;
  groupBy?: never;
  renderGroupHeading?: never;
  isVirtualized?: never;
}

export type SingleComboboxProps<T extends DataWithId> =
  | SingleComboboxDataProps<T>
  | SingleComboboxVirtualizedProps<T>
  | SingleComboboxChildrenProps;

// ─── Component ────────────────────────────────────────────────────────────────

export function SingleCombobox<T extends DataWithId>(
  props: SingleComboboxProps<T>
) {
  const {
    id,
    placeholder = "Search...",
    emptyText = "No results found.",
    filter,
    fullWidth = true,
  } = props;
  const ariaDescribedBy = props["aria-describedby"];
  const ariaLabel = props["aria-label"];
  const ariaLabelledBy = props["aria-labelledby"];
  const isInvalid = props.isInvalid;

  const isChildrenMode = "children" in props && props.children != null;

  type DataProps =
    | SingleComboboxDataProps<T>
    | SingleComboboxVirtualizedProps<T>;

  const data = isChildrenMode ? ([] as T[]) : (props as DataProps).data;
  const itemToString = isChildrenMode
    ? (_item: T | null) => ""
    : (props as DataProps).itemToString;
  const renderItem = isChildrenMode
    ? undefined
    : (props as DataProps).renderItem;
  const inputType = isChildrenMode
    ? "icon"
    : (props as DataProps).inputType ?? "icon";
  const isVirtualized = isChildrenMode
    ? false
    : (props as DataProps).isVirtualized ?? false;
  const groupBy = isChildrenMode
    ? undefined
    : isVirtualized
    ? undefined
    : (props as SingleComboboxDataProps<T>).groupBy;
  const renderGroupHeading = isChildrenMode
    ? undefined
    : isVirtualized
    ? undefined
    : (props as SingleComboboxDataProps<T>).renderGroupHeading;
  const children = isChildrenMode
    ? (props as SingleComboboxChildrenProps).children
    : undefined;

  const selectedItemId = props.selectedItemId;
  const defaultSelectedItemId = props.defaultSelectedItemId;
  const onSelectedItemIdChange = props.onSelectedItemIdChange;

  const { containerRef, portalContainer } = useSeedsPortalContainer();
  const [open, setOpen] = React.useState(false);
  const virtualizerRef = React.useRef<ReturnType<
    typeof useVirtualizer<HTMLDivElement, Element>
  > | null>(null);

  const onInputKeyDownCapture = useSuppressEscapeClear(open);

  const isControlled = selectedItemId !== undefined;

  const idToItem = React.useCallback(
    (id: T["id"] | null | undefined): T | null => {
      if (id == null) return null;
      return data.find((d) => String(d.id) === String(id)) ?? null;
    },
    [data]
  );

  const [internalValue, setInternalValue] = React.useState<T | null>(() =>
    idToItem(defaultSelectedItemId as T["id"] | null)
  );

  const value = isControlled
    ? idToItem(selectedItemId as T["id"] | null)
    : internalValue;

  function handleValueChange(newItem: T | null) {
    if (!isControlled) {
      setInternalValue(newItem);
    }
    if (onSelectedItemIdChange) {
      onSelectedItemIdChange(newItem ? newItem.id : null);
    }
  }

  const groups = React.useMemo(
    () => (groupBy ? groupItems(data, groupBy) : null),
    [data, groupBy]
  );

  return (
    <Field $fullWidth={fullWidth}>
      <div ref={containerRef} style={{ position: "relative" }} />
      <Combobox.Root
        id={id}
        value={value}
        onValueChange={handleValueChange}
        itemToStringLabel={itemToString}
        isItemEqualToValue={(a, b) => a != null && b != null && a.id === b.id}
        items={
          isVirtualized ? data : groups ?? (isChildrenMode ? undefined : data)
        }
        virtualized={isVirtualized}
        disabled={props.disabled}
        filter={filter}
        open={open}
        onOpenChange={setOpen}
        onItemHighlighted={
          isVirtualized
            ? (item, { reason, index }) => {
                const virt = virtualizerRef.current;
                if (!item || !virt) return;
                const isStart = index === 0;
                const isEnd = index === virt.options.count - 1;
                if (reason === "keyboard" && (isStart || isEnd)) {
                  queueMicrotask(() => {
                    virt.scrollToIndex(index, {
                      align: isStart ? "start" : "end",
                    });
                  });
                }
              }
            : undefined
        }
      >
        <ComboboxInputGroup
          $isInvalid={isInvalid}
          onKeyDownCapture={onInputKeyDownCapture}
        >
          <ComboboxInput
            placeholder={placeholder}
            id={id}
            aria-describedby={ariaDescribedBy}
            aria-invalid={isInvalid || undefined}
            aria-required={props.required || undefined}
          />
          <ComboboxActionButtons>
            <ComboboxClear aria-label="Clear selection">
              <ClearIcon />
            </ComboboxClear>
            <ComboboxTrigger aria-label="Open popup">
              <StyledChevron $isOpen={open}>
                <ChevronIcon />
              </StyledChevron>
            </ComboboxTrigger>
          </ComboboxActionButtons>
        </ComboboxInputGroup>

        <Combobox.Portal container={portalContainer}>
          <ComboboxPositioner sideOffset={4}>
            <ComboboxPopup
              style={
                isVirtualized
                  ? { display: "flex", flexDirection: "column" }
                  : undefined
              }
            >
              <ComboboxEmpty>{emptyText}</ComboboxEmpty>
              <ComboboxList
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
                style={
                  isVirtualized
                    ? {
                        padding: 0,
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                      }
                    : undefined
                }
              >
                {isVirtualized ? (
                  <VirtualizedComboboxList
                    open={open}
                    virtualizerRef={virtualizerRef}
                    value={value}
                    groups={groups}
                    data={data}
                    itemToString={itemToString}
                    inputType={inputType}
                    renderItem={renderItem}
                    renderGroupHeading={renderGroupHeading}
                  />
                ) : children ? (
                  children
                ) : groups ? (
                  (group: { value: string; items: T[] }, index: number) => (
                    <React.Fragment key={group.value}>
                      <ComboboxGroup items={group.items}>
                        <ComboboxGroupLabel>
                          {renderGroupHeading
                            ? renderGroupHeading(group.value)
                            : group.value}
                        </ComboboxGroupLabel>
                        <Combobox.Collection>
                          {(item: T) => (
                            <ComboboxItem
                              key={item.id}
                              value={item}
                              itemId={String(item.id)}
                              label={itemToString(item)}
                              disabled={item.disabled}
                              inputType={inputType}
                              renderItem={
                                renderItem
                                  ? () => renderItem(item)
                                  : () => itemToString(item)
                              }
                            />
                          )}
                        </Combobox.Collection>
                      </ComboboxGroup>
                      {index < groups.length - 1 && <ComboboxSeparator />}
                    </React.Fragment>
                  )
                ) : (
                  (item: T) => (
                    <ComboboxItem
                      key={item.id}
                      value={item}
                      itemId={String(item.id)}
                      label={itemToString(item)}
                      disabled={item.disabled}
                      inputType={inputType}
                      renderItem={
                        renderItem
                          ? () => renderItem(item)
                          : () => itemToString(item)
                      }
                    />
                  )
                )}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </Combobox.Portal>
      </Combobox.Root>
    </Field>
  );
}
